import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

// Lightweight, dependency-free chart primitives for Cinema Intelligence.
// CSS/flow-based so they are responsive and inherit the Cphere dark-card system.
// Native `title` attributes provide hover tooltips without a charting library.

// ---------------------------------------------------------------------------
// Exhibitor distribution donut.
//
// Geometry, the interaction contract and the viewport-clamped tooltip placement
// are ported from the Campaign Strategy microsite's plan donut
// (SherlocMediaPlanCharts.js: `DonutChart`, `getChartInteractionProps`,
// `calculateChartPopoverPosition`) so the two surfaces behave identically.
// The segment palette is deliberately NOT the microsite's: see below.
//
// It is a PORT, not an import, on purpose: that module has diverged between the
// Cortex and Quantum portals, and this file is asserted byte-identical across
// both. Importing a diverged page module here would make a shared component
// behave differently per portal.
//
// This module NEVER aggregates. It renders whatever exhibitor model it is
// handed — the same `model.exhibitors` array that feeds the horizontal bars —
// so the donut and the bars cannot disagree about counts or percentages.
// ---------------------------------------------------------------------------

// Radius 42 on a 120x120 viewBox: 2 * PI * 42, matching the microsite donut.
const DONUT_CIRCUMFERENCE = 263.89;
// The microsite's dash sweep starts a quarter turn back so the first segment
// begins at twelve o'clock.
const DONUT_START_OFFSET = 25;
// A governed footprint can legitimately contain a single-cinema exhibitor
// (1.3% of 75 = 3.4 units of arc). Below this the stroke stops reading as a
// segment at all, so it is floored for RENDERING only. Reported counts and
// percentages are never touched by this.
const DONUT_MIN_VISIBLE_ARC = 2.4;

// Cinema Analytics' own neon vocabulary comes first — magenta and periwinkle
// are the `.cinema-bar-fill` gradient stops, cyan and gold are the parity-layer
// accents — so the ring reads as part of this page rather than as a chart
// borrowed from another surface. The microsite's remaining hues fill the tail
// for footprints with more exhibitors than the page palette has colours.
export const CINEMA_EXHIBITOR_SEGMENT_COLORS = [
  "#c24de7",
  "#7ee7ff",
  "#889ce7",
  "#e0b64f",
  "#df5f7a",
  "#55d07f",
  "#8f63e8",
  "#f39a34",
];

/**
 * The box `calculateCinemaChartTooltipPosition` reserves for the chart tooltip.
 *
 * `width` is a CONTRACT with `.cinema-chart-tooltip`, not a guess: the rule
 * declares `width: min(240px, 86vw)` under `box-sizing: border-box`, so 240 is
 * the painted OUTER width and the clamp reserves exactly what the browser
 * paints. Exported so a test can hold the two numbers together — they drifted
 * apart once already, when content-box sizing painted 268px against a reserved
 * 240 and pushed the tooltip 28px past the edge the clamp had just cleared.
 *
 * `height` is a deliberate upper bound rather than an exact figure: the real
 * painted height depends on how the exhibitor label wraps (86px for every
 * governed chain name today, 116px for a 50-character one). Reserving more
 * than is painted is the safe direction — it can only flip the tooltip above
 * sooner than strictly necessary, never let it hang off the bottom.
 */
export const CINEMA_CHART_TOOLTIP_SIZE = Object.freeze({ width: 240, height: 132 });
const TOOLTIP_SIZE = CINEMA_CHART_TOOLTIP_SIZE;
export const CINEMA_CHART_TOOLTIP_GAP = 12;
export const CINEMA_CHART_TOOLTIP_VIEWPORT_PADDING = 12;
const TOOLTIP_GAP = CINEMA_CHART_TOOLTIP_GAP;
const TOOLTIP_VIEWPORT_PADDING = CINEMA_CHART_TOOLTIP_VIEWPORT_PADDING;

// State breakdown popover. Its height depends on how many exhibitors a state
// contains, so it is measured from the row count rather than fixed — the
// placement maths needs a real height to decide whether to flip above, and a
// stale constant would flip the wrong way for a five-exhibitor state.
const STATE_TOOLTIP_WIDTH = 272;
const STATE_TOOLTIP_HEADER_HEIGHT = 52;
const STATE_TOOLTIP_ROW_HEIGHT = 30;
const STATE_TOOLTIP_VERTICAL_PADDING = 22;
// Above this the list scrolls inside the popover instead of growing further,
// which is what keeps a many-exhibitor state from running past the viewport.
export const STATE_TOOLTIP_MAX_HEIGHT = 268;

export function resolveCinemaSegmentColor(index) {
  const position = Number.isInteger(index) && index >= 0 ? index : 0;
  return CINEMA_EXHIBITOR_SEGMENT_COLORS[
    position % CINEMA_EXHIBITOR_SEGMENT_COLORS.length
  ];
}

/**
 * Turn the canonical exhibitor aggregation into donut geometry.
 *
 * `exhibitors` is the model array the horizontal bars already consume; this
 * function adds colour and arc lengths and nothing else. Counts and
 * percentages pass through untouched, which is what makes the two charts
 * reconcile by construction rather than by coincidence.
 */
export function buildCinemaExhibitorDistribution(exhibitors, totalCinemas) {
  const groups = Array.isArray(exhibitors) ? exhibitors : [];
  const total =
    Number(totalCinemas) > 0
      ? Number(totalCinemas)
      : groups.reduce((sum, group) => sum + (Number(group?.cinemaCount) || 0), 0);

  if (!total || !groups.length) {
    return { segments: [], total: 0, hasData: false };
  }

  let cursor = DONUT_START_OFFSET;
  const segments = groups.map((group, index) => {
    const cinemaCount = Number(group?.cinemaCount) || 0;
    const trueArc = (cinemaCount / total) * DONUT_CIRCUMFERENCE;
    const arc =
      trueArc > 0 && trueArc < DONUT_MIN_VISIBLE_ARC ? DONUT_MIN_VISIBLE_ARC : trueArc;
    const segment = {
      key: group?.key || group?.label || `exhibitor-${index}`,
      label: group?.label || "Unspecified",
      cinemaCount,
      // Percentage is the model's own — never recomputed here.
      percentage: Number(group?.percentage) || 0,
      isIndependent: group?.isIndependent === true,
      image: group?.image || null,
      // Carried, never re-derived: the caller's artwork resolver already knows
      // whether this is the shared mark or a chain's own logo.
      imageIsFallback: group?.imageIsFallback === true,
      color: resolveCinemaSegmentColor(index),
      arc,
      offset: -cursor,
    };
    cursor += arc;
    return segment;
  });

  return { segments, total, hasData: true };
}

/**
 * Viewport-clamped tooltip placement, ported from the microsite's
 * `calculateChartPopoverPosition`. Flips above the anchor when there is no room
 * below and clamps on both axes, so a segment near an edge can never push the
 * tooltip off screen or widen the page.
 */
export function calculateCinemaChartTooltipPosition(
  anchorRect,
  viewport = {},
  tooltipSize = TOOLTIP_SIZE
) {
  if (!anchorRect) {
    return {
      top: TOOLTIP_VIEWPORT_PADDING,
      left: TOOLTIP_VIEWPORT_PADDING,
      placement: "below",
    };
  }
  const viewportWidth = Number(viewport.width) || 1024;
  const viewportHeight = Number(viewport.height) || 768;
  const scrollX = Number(viewport.scrollX) || 0;
  const scrollY = Number(viewport.scrollY) || 0;
  const width = Number(tooltipSize.width) || TOOLTIP_SIZE.width;
  const height = Number(tooltipSize.height) || TOOLTIP_SIZE.height;
  const minLeft = scrollX + TOOLTIP_VIEWPORT_PADDING;
  const maxLeft = scrollX + viewportWidth - width - TOOLTIP_VIEWPORT_PADDING;
  const centeredLeft = scrollX + anchorRect.left + anchorRect.width / 2 - width / 2;
  const left = Math.min(Math.max(centeredLeft, minLeft), Math.max(minLeft, maxLeft));
  const belowTop = scrollY + anchorRect.bottom + TOOLTIP_GAP;
  const aboveTop = scrollY + anchorRect.top - height - TOOLTIP_GAP;
  const fitsBelow =
    belowTop + height <= scrollY + viewportHeight - TOOLTIP_VIEWPORT_PADDING;
  const fitsAbove = aboveTop >= scrollY + TOOLTIP_VIEWPORT_PADDING;
  const placement = fitsBelow || !fitsAbove ? "below" : "above";
  const unclampedTop = placement === "below" ? belowTop : aboveTop;
  const minTop = scrollY + TOOLTIP_VIEWPORT_PADDING;
  const maxTop = scrollY + viewportHeight - height - TOOLTIP_VIEWPORT_PADDING;
  const top = Math.min(Math.max(unclampedTop, minTop), Math.max(minTop, maxTop));
  return { top, left, placement };
}

export function buildCinemaExhibitorTooltipLines(segment, total) {
  const count = Number(segment?.cinemaCount) || 0;
  const denominator = Number(total) || 0;
  return [
    `${count} of ${denominator} ${denominator === 1 ? "cinema" : "cinemas"}`,
    `${formatFootprintShare(segment?.percentage)} of footprint`,
  ];
}

// Presentation-only rounding, matched to the bars so both read the same.
function formatFootprintShare(percentage) {
  const value = Number(percentage);
  if (!Number.isFinite(value)) return "0%";
  return `${Math.round(value * 10) / 10}%`;
}

function readAnchorRect(target) {
  const rect = target?.getBoundingClientRect?.();
  if (rect) return rect;
  return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
}

function CinemaChartTooltip({ segment, total, position, id }) {
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      id={id}
      role="tooltip"
      className={`cinema-chart-tooltip cinema-chart-tooltip--${position.placement}`}
      style={{ top: position.top, left: position.left }}
    >
      <strong className="cinema-chart-tooltip-title">
        {segment.image ? (
          <span className="cinema-chart-tooltip-logo" aria-hidden="true">
            <img src={segment.image} alt="" />
          </span>
        ) : null}
        <span className="cinema-chart-tooltip-title-text">{segment.label}</span>
      </strong>
      {buildCinemaExhibitorTooltipLines(segment, total).map((line) => (
        <span className="cinema-chart-tooltip-line" key={line}>
          {line}
        </span>
      ))}
    </div>,
    document.body
  );
}

export function CinemaExhibitorDonutChart({
  distribution,
  emptyMessage = "Exhibitor distribution populates once a governed footprint is saved.",
}) {
  const [active, setActive] = useState(null);
  const segments = distribution?.segments || [];
  const total = distribution?.total || 0;

  const show = useCallback((segment, target) => {
    setActive({ segment, anchorRect: readAnchorRect(target) });
  }, []);
  const hide = useCallback(() => setActive(null), []);

  // Tap-outside and Escape both dismiss, so a touch user is never stranded
  // with an open tooltip they cannot close.
  useEffect(() => {
    if (!active || typeof document === "undefined") return undefined;
    const onPointerDown = (event) => {
      if (event.target?.closest?.('[data-cinema-chart-trigger="true"]')) return;
      hide();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") hide();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [active, hide]);

  if (!distribution?.hasData || !segments.length) {
    return <div className="cinema-chart-empty">{emptyMessage}</div>;
  }

  const position = active
    ? calculateCinemaChartTooltipPosition(active.anchorRect, {
        width: typeof window === "undefined" ? 1024 : window.innerWidth,
        height: typeof window === "undefined" ? 768 : window.innerHeight,
        scrollX: typeof window === "undefined" ? 0 : window.scrollX,
        scrollY: typeof window === "undefined" ? 0 : window.scrollY,
      })
    : null;

  return (
    <div className="cinema-donut-wrap">
      <svg viewBox="0 0 120 120" className="cinema-donut" role="img"
        aria-label={`Exhibitor distribution across ${total} governed cinemas`}>
        <circle className="cinema-donut-track" cx="60" cy="60" r="42" />
        {segments.map((segment) => {
          const describedBy = `cinema-donut-tip-${segment.key}`;
          const trigger = (event) => show(segment, event.currentTarget);
          return (
            <g
              key={segment.key}
              className="cinema-donut-segment-item"
              tabIndex={0}
              role="button"
              data-cinema-chart-trigger="true"
              aria-label={`${segment.label}, ${
                buildCinemaExhibitorTooltipLines(segment, total).join(", ")
              }`}
              aria-describedby={active?.segment?.key === segment.key ? describedBy : undefined}
              onMouseEnter={trigger}
              onMouseMove={trigger}
              onFocus={trigger}
              onClick={trigger}
              onMouseLeave={hide}
              onBlur={hide}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  hide();
                }
              }}
            >
              <circle
                className="cinema-donut-segment"
                cx="60"
                cy="60"
                r="42"
                stroke={segment.color}
                strokeDasharray={`${segment.arc} ${DONUT_CIRCUMFERENCE - segment.arc}`}
                strokeDashoffset={segment.offset}
              >
                {/* Native title keeps the data reachable even if JS tooltips
                    are suppressed by the environment. */}
                <title>{`${segment.label}: ${
                  buildCinemaExhibitorTooltipLines(segment, total).join(" · ")
                }`}</title>
              </circle>
            </g>
          );
        })}
        <text x="60" y="57" className="cinema-donut-center-main">
          {total}
        </text>
        <text x="60" y="69" className="cinema-donut-center-sub">
          {total === 1 ? "cinema" : "cinemas"}
        </text>
      </svg>
      {active && position ? (
        <CinemaChartTooltip
          segment={active.segment}
          total={total}
          position={position}
          id={`cinema-donut-tip-${active.segment.key}`}
        />
      ) : null}
    </div>
  );
}

export function CinemaExhibitorLegend({ distribution }) {
  const segments = distribution?.segments || [];
  const total = distribution?.total || 0;
  if (!segments.length) return null;
  return (
    <div className="cinema-donut-legend" role="list">
      {segments.map((segment) => (
        <div className="cinema-donut-legend-row" role="listitem" key={segment.key}>
          <span className="cinema-donut-legend-media" aria-hidden="true">
            {segment.image ? (
              <img
                src={segment.image}
                alt=""
                className={
                  segment.imageIsFallback ? "cinema-chart-mark--fallback" : undefined
                }
              />
            ) : null}
          </span>
          <span className="cinema-donut-legend-identity">
            <span className="cinema-donut-legend-label">{segment.label}</span>
            <span className="cinema-donut-legend-count">
              {segment.cinemaCount} {segment.cinemaCount === 1 ? "cinema" : "cinemas"}
            </span>
          </span>
          <span className="cinema-donut-legend-share cinema-analysis-numeric">
            {formatFootprintShare(segment.percentage)}
          </span>
          <span
            className="cinema-donut-legend-swatch"
            style={{ backgroundColor: segment.color }}
            aria-hidden="true"
          />
        </div>
      ))}
      <span className="cinema-donut-legend-total">
        {total} governed {total === 1 ? "cinema" : "cinemas"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Geographic footprint: state tile grid with an exhibitor-breakdown popover.
//
// The tiles answer "how many cinemas are in this state?"; the popover answers
// the follow-up, "which exhibitors make up that total?". It is an additive
// interaction layer — tile geometry, counts, shading and the governed
// denominator are all unchanged, and a tile navigates nowhere.
//
// Like the donut, this component NEVER aggregates and never resolves artwork.
// It renders the per-state exhibitor breakdown the canonical model already
// carries, with the artwork the page's single resolver already attached.
// ---------------------------------------------------------------------------

/**
 * Popover size for a state with `exhibitorCount` rows.
 *
 * Height is measured rather than fixed because it feeds
 * `calculateCinemaChartTooltipPosition`, which decides flip-above from it. It
 * is capped at `STATE_TOOLTIP_MAX_HEIGHT`, matching the CSS max-height, so the
 * clamp maths and the rendered box agree about how tall the popover really is.
 */
export function measureCinemaStateTooltipSize(exhibitorCount) {
  const rows = Math.max(0, Number(exhibitorCount) || 0);
  const natural =
    STATE_TOOLTIP_HEADER_HEIGHT +
    STATE_TOOLTIP_VERTICAL_PADDING +
    rows * STATE_TOOLTIP_ROW_HEIGHT;
  return {
    width: STATE_TOOLTIP_WIDTH,
    height: Math.min(natural, STATE_TOOLTIP_MAX_HEIGHT),
  };
}

/**
 * The breakdown as one sentence, for the tile's accessible name and its native
 * `title`. Counts and shares are the model's own — this only reads them out.
 */
export function describeCinemaStateBreakdown(state) {
  const count = Number(state?.cinemaCount) || 0;
  const noun = count === 1 ? "cinema" : "cinemas";
  const exhibitors = Array.isArray(state?.exhibitors) ? state.exhibitors : [];
  const head = `${state?.stateName || state?.key || "State"}, ${
    state?.countLabel ?? count
  } governed ${noun}`;
  if (!exhibitors.length) return head;
  const rows = exhibitors
    .map(
      (exhibitor) =>
        `${exhibitor.label} ${
          exhibitor.countLabel ?? exhibitor.cinemaCount
        } (${formatFootprintShare(exhibitor.percentage)})`
    )
    .join(", ");
  return `${head}: ${rows}`;
}

function CinemaStateBreakdownTooltip({ state, position, id }) {
  if (typeof document === "undefined") return null;
  const count = Number(state?.cinemaCount) || 0;
  const exhibitors = Array.isArray(state?.exhibitors) ? state.exhibitors : [];
  return createPortal(
    <div
      id={id}
      role="tooltip"
      className={`cinema-chart-tooltip cinema-geo-tooltip cinema-chart-tooltip--${position.placement}`}
      style={{ top: position.top, left: position.left }}
    >
      <strong className="cinema-chart-tooltip-title cinema-geo-tooltip-title">
        <span className="cinema-chart-tooltip-title-text">{state.stateName}</span>
        <span className="cinema-geo-tooltip-total cinema-analysis-numeric">
          {state.countLabel ?? count} {count === 1 ? "cinema" : "cinemas"}
        </span>
      </strong>
      <div className="cinema-geo-tooltip-rows" role="list">
        {exhibitors.map((exhibitor) => (
          <div className="cinema-geo-tooltip-row" role="listitem" key={exhibitor.key}>
            <span className="cinema-geo-tooltip-media" aria-hidden="true">
              {exhibitor.image ? (
                <img
                  src={exhibitor.image}
                  alt=""
                  className={
                    exhibitor.imageIsFallback ? "cinema-chart-mark--fallback" : undefined
                  }
                  loading="lazy"
                  draggable={false}
                />
              ) : null}
            </span>
            <span className="cinema-geo-tooltip-label">{exhibitor.label}</span>
            <span className="cinema-geo-tooltip-value cinema-analysis-numeric">
              {exhibitor.countLabel ?? exhibitor.cinemaCount}
              <span className="cinema-geo-tooltip-share">
                {" "}
                · {formatFootprintShare(exhibitor.percentage)}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
}

export function CinemaGeographicTileGrid({
  tiles,
  columns,
  rows,
  maxCinemaCount,
  ariaLabel,
}) {
  const [active, setActive] = useState(null);
  const cells = Array.isArray(tiles) ? tiles : [];

  const show = useCallback((state, target) => {
    setActive({ state, anchorRect: readAnchorRect(target) });
  }, []);
  const hide = useCallback(() => setActive(null), []);

  // Same dismissal contract as the donut: tap-outside and Escape both close,
  // so a touch user is never stranded with an open popover.
  useEffect(() => {
    if (!active || typeof document === "undefined") return undefined;
    const onPointerDown = (event) => {
      if (event.target?.closest?.('[data-cinema-chart-trigger="true"]')) return;
      hide();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") hide();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [active, hide]);

  const ceiling = Number(maxCinemaCount) || 0;
  const position = active
    ? calculateCinemaChartTooltipPosition(
        active.anchorRect,
        {
          width: typeof window === "undefined" ? 1024 : window.innerWidth,
          height: typeof window === "undefined" ? 768 : window.innerHeight,
          scrollX: typeof window === "undefined" ? 0 : window.scrollX,
          scrollY: typeof window === "undefined" ? 0 : window.scrollY,
        },
        measureCinemaStateTooltipSize(active.state?.exhibitors?.length)
      )
    : null;

  return (
    <div
      className="cinema-geo-tile-grid"
      role="group"
      aria-label={ariaLabel}
      style={{
        "--cinema-geo-grid-cols": columns,
        "--cinema-geo-grid-rows": rows,
      }}
    >
      {cells.map((cell) => {
        const state = cell.state || null;
        const intensity = state && ceiling ? state.cinemaCount / ceiling : 0;
        const style = {
          gridRow: cell.row,
          gridColumn: cell.col,
          "--cinema-geo-share": intensity,
        };
        // An inert state has nothing to break down, so it stays a plain,
        // unfocusable tile rather than a button that opens an empty popover.
        if (!state) {
          return (
            <div
              key={cell.stateKey}
              className="cinema-geo-tile"
              style={style}
              title={`${cell.stateKey} · no governed cinemas`}
            >
              <span className="cinema-geo-tile-state">{cell.stateKey}</span>
            </div>
          );
        }
        const describedBy = `cinema-geo-tip-${cell.stateKey}`;
        const summary = describeCinemaStateBreakdown(state);
        const trigger = (event) => show(state, event.currentTarget);
        return (
          <button
            type="button"
            key={cell.stateKey}
            className="cinema-geo-tile cinema-geo-tile--active"
            style={style}
            data-cinema-chart-trigger="true"
            title={summary}
            aria-label={summary}
            aria-describedby={active?.state?.key === state.key ? describedBy : undefined}
            onMouseEnter={trigger}
            onFocus={trigger}
            onClick={trigger}
            onMouseLeave={hide}
            onBlur={hide}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                hide();
              }
            }}
          >
            <span className="cinema-geo-tile-state">{cell.stateKey}</span>
            <span className="cinema-geo-tile-count cinema-analysis-numeric">
              {state.countLabel ?? state.cinemaCount}
            </span>
          </button>
        );
      })}
      {active && position ? (
        <CinemaStateBreakdownTooltip
          state={active.state}
          position={position}
          id={`cinema-geo-tip-${active.state.key}`}
        />
      ) : null}
    </div>
  );
}

export function CinemaInsightChartCard({ title, description, badge, children }) {
  return (
    <div className="cinema-chart-card">
      <div className="cinema-chart-card-header">
        <div className="cinema-chart-card-heading">
          <div className="cinema-chart-card-title">{title}</div>
          {description ? <div className="cinema-chart-card-desc">{description}</div> : null}
        </div>
        {badge || null}
      </div>
      <div className="cinema-chart-card-body">{children}</div>
    </div>
  );
}

export function CinemaHorizontalBarChart({ data, emptyMessage = "No data available yet." }) {
  const items = Array.isArray(data?.items) ? data.items : [];
  if (!items.length) {
    return <div className="cinema-chart-empty">{emptyMessage}</div>;
  }

  const max = Math.max(...items.map((item) => item.value || 0), 1);

  return (
    <div className="cinema-bar-chart" role="img" aria-label="Horizontal bar chart">
      {items.map((item) => {
        const width = Math.max(2, Math.round(((item.value || 0) / max) * 100));
        const percentText = typeof item.percent === "number" ? ` (${item.percent}%)` : "";
        return (
          <div
            className={`cinema-bar-row${item.image ? " cinema-bar-row--with-media" : ""}`}
            key={item.label}
            title={`${item.label}: ${item.value}${percentText}`}
          >
            {/* Optional: only the exhibitor bars carry artwork. Every other
                caller of this chart omits `image` and keeps the two-column
                label/track/value rhythm it has always had. */}
            {item.image ? (
              <span className="cinema-bar-media" aria-hidden="true">
                <img
                  src={item.image}
                  alt=""
                  className={
                    item.imageIsFallback ? "cinema-chart-mark--fallback" : undefined
                  }
                  loading="lazy"
                  draggable={false}
                />
              </span>
            ) : null}
            <div className="cinema-bar-label">{item.label}</div>
            <div className="cinema-bar-track">
              <div
                className={`cinema-bar-fill${item.isOther ? " cinema-bar-fill-muted" : ""}`}
                style={{ width: `${width}%` }}
              />
            </div>
            <div className="cinema-bar-value">
              {item.value}
              {typeof item.percent === "number" ? (
                <span className="cinema-bar-percent"> · {item.percent}%</span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function CinemaReadinessChart({ data, emptyMessage = "Readiness will populate after upload." }) {
  const segments = (data?.segments || []).filter((seg) => (seg.value || 0) > 0);
  const total = data?.total || 0;

  if (!total) {
    return <div className="cinema-chart-empty">{emptyMessage}</div>;
  }

  return (
    <div className="cinema-readiness-chart">
      <div className="cinema-readiness-bar" role="img" aria-label="Location readiness distribution">
        {segments.map((seg) => (
          <div
            key={seg.key}
            className="cinema-readiness-seg"
            style={{ width: `${(seg.value / total) * 100}%`, background: seg.color }}
            title={`${seg.label}: ${seg.value}`}
          />
        ))}
      </div>
      <div className="cinema-readiness-legend">
        {segments.map((seg) => (
          <div className="cinema-readiness-legend-item" key={seg.key}>
            <span className="cinema-readiness-dot" style={{ background: seg.color }} />
            <span className="cinema-readiness-legend-label">{seg.label}</span>
            <span className="cinema-readiness-legend-value">{seg.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
